/**
 * Minimal, dependency-free replacement for the `geojson-to-svg` package.
 *
 * `geojson-to-svg` pulled in `nightmare` -> `electron@2` as an optional dependency (used only by
 * its `measure-font` CLI, never by the rendering API), which surfaced dozens of Dependabot
 * advisories. We only ever rendered polygonal geometries with a flat fill/stroke, so this module
 * reimplements exactly that slice.
 *
 * Output is intentionally byte-identical to the old library's for the options we used, so existing
 * markup and snapshots keep rendering the same.
 */

type PolygonalGeometry = GeoJSON.Polygon | GeoJSON.MultiPolygon;

export interface GeoJsonToSvgOptions {
  /** Polygon fill; omitted renders `fill="none"`. */
  fill?: string;
  /** Polygon stroke; omitted renders `stroke="none"`. */
  stroke?: string;
  /** Overrides the geometry-derived `viewBox`; `"minX minY width height"`. */
  viewBox?: string;
}

const XMLNS = 'http://www.w3.org/2000/svg';
const XLINK = 'http://www.w3.org/1999/xlink';
const SVG_VERSION = '1.2';

/** `[minX, minY, maxX, maxY]`, seeded so the first coordinate replaces every bound. */
type Bounds = [number, number, number, number];

const createBounds = (): Bounds => [Infinity, Infinity, -Infinity, -Infinity];

const extendBounds = (bounds: Bounds, [x, y]: GeoJSON.Position): void => {
  bounds[0] = Math.min(x, bounds[0]);
  bounds[1] = Math.min(y, bounds[1]);
  bounds[2] = Math.max(x, bounds[2]);
  bounds[3] = Math.max(y, bounds[3]);
};

const isFiniteBounds = (bounds: Bounds): boolean => bounds.every(Number.isFinite);

/** A closed `M .. L .. Z` subpath for a single linear ring. */
const ringToPath = (ring: GeoJSON.Position[], bounds: Bounds): string => {
  let path = '';

  for (const [index, position] of ring.entries()) {
    path += `${index === 0 ? 'M' : 'L'}${position[0]} ${position[1]}`;
    extendBounds(bounds, position);
  }

  return path ? `${path}Z` : '';
};

/**
 * Rings are prefixed with a space and the whole path trimmed, which is what produces the old
 * library's single space between a polygon's rings and double space between multipolygon parts.
 */
const ringsToPath = (rings: GeoJSON.Position[][], bounds: Bounds): string =>
  rings.map((ring) => ` ${ringToPath(ring, bounds)}`).join('');

const geometryToPath = (geometry: PolygonalGeometry, bounds: Bounds): string => {
  const path =
    geometry.type === 'Polygon'
      ? ringsToPath(geometry.coordinates, bounds)
      : geometry.coordinates.map((rings) => ` ${ringsToPath(rings, bounds)}`).join('');

  return path.trim() || 'M0 0';
};

/** Formats bounds as an SVG `viewBox` value: `"minX minY width height"`. */
const boundsToViewBox = (bounds: Bounds): string =>
  isFiniteBounds(bounds)
    ? [bounds[0], bounds[1], bounds[2] - bounds[0], bounds[3] - bounds[1]].join(' ')
    : '0 0 0 0';

const styleAttributes = (geometry: PolygonalGeometry, { fill, stroke }: GeoJsonToSvgOptions) => {
  const attributes = stroke
    ? ` stroke="${stroke}" stroke-linecap="round" stroke-linejoin="round"`
    : ' stroke="none"';

  if (!fill) return `${attributes} fill="none"`;

  // Multipolygons may carry disjoint parts wound the same way, so they need `nonzero`.
  const fillRule = geometry.type === 'MultiPolygon' ? 'nonzero' : 'evenodd';

  return `${attributes} fill="${fill}" fill-opacity="" fill-rule="${fillRule}"`;
};

/**
 * Renders a polygonal geometry as a standalone SVG string in the geometry's own coordinate space.
 *
 * The y axis is *not* flipped, so callers rendering geographic coordinates still need the
 * `scale(1, -1)` transform they applied before.
 */
export const geoJsonToSvg = (
  geometry: PolygonalGeometry,
  options: GeoJsonToSvgOptions = {}
): string => {
  const bounds = createBounds();
  const path = geometryToPath(geometry, bounds);
  const viewBox = options.viewBox ?? boundsToViewBox(bounds);

  return (
    `<svg viewBox="${viewBox}" xmlns="${XMLNS}" xmlns:xlink="${XLINK}" version="${SVG_VERSION}">` +
    `<g><path class="polygon" d="${path}"${styleAttributes(geometry, options)}/></g></svg>`
  );
};

/**
 * The `viewBox` `geoJsonToSvg` would derive, without building the markup.
 *
 * @returns `"minX minY width height"`
 */
export const geoJsonViewBox = (geometry: PolygonalGeometry): string => {
  const bounds = createBounds();
  geometryToPath(geometry, bounds);

  return boundsToViewBox(bounds);
};
